import moment from 'moment';
import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { ResponsiveImage } from '../../../components';
import { createSlug } from '../../../util/urlHelpers';
import { getAgeRange } from '../../../util/ageUtils';
import css from './ProductCard.module.css';
import { AiFillStar } from 'react-icons/ai';
import classNames from 'classnames';
import Decimal from 'decimal.js';
import NoIGLinkPopup from '../../NoIGLinkPopup/NoIGLinkPopup';
import { shouldUseGeolocation } from '../../../util/geolocation/config';
import useIpGeolocation from '../../../util/geolocation/useIpGeolocation';

function ProductCard({
  title,
  id,
  description,
  price,
  images,
  publicData = {},
  metadata,
  customImage,
  intl,
  authorName,
  geolocation,
  profileMetadata,
  imageLoading = 'lazy',
  imageFetchPriority = 'auto',
}) {
  // State to manage popup visibility
  const [showPopup, setShowPopup] = useState(false);
  
  // Check if the listing belongs to a profile that should use geolocation
  const shouldUseGeo = useMemo(() => {
    return shouldUseGeolocation(authorName, id);
  }, [authorName, id]);

  // Use our custom hook for geolocation (only activates for configured profiles)
  useIpGeolocation(shouldUseGeo);

  const stars = metadata?.avgRating;
  const avgRating = stars ? new Decimal(stars).div(100).toNumber() : null;

  // Function to toggle popup
  const togglePopup = () => setShowPopup(!showPopup);

  const history = useHistory();
  const { dob, gender } = publicData;
  const getTitle = title => (title?.length > 25 ? title.substring(0, 15) + '...' : title);

  const dateOfBirth = moment(dob, 'YYYY-MM-DD');
  const exactAge = dateOfBirth
    ? parseInt(moment.duration(moment().diff(dateOfBirth)).asYears(), 10)
    : null;
  const ageRange = getAgeRange(exactAge);

  const handleClick = () => {
    history.push({
      pathname: `/l/${createSlug(title)}/${id}`,
    });
  };

  // Create accessible card title for screen readers
  const cardTitle = `${authorName?.toLowerCase() || 'Tester'}, ${ageRange || ''} ${gender?.toLowerCase() || ''}`;
  
  // TEMP: Force display 5 star rating for UI testing
  // To disable: Comment out the next line and uncomment the one below it
  // const displayRating = 4.2; 
  const displayRating = avgRating;

  return (
    <div className={css.product} role="article" aria-label={cardTitle}>
      <div onClick={handleClick} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div className={classNames(css.productImageWrapper, 'relative')}>
          <div className={css.imageOverlay} />
          {images && images[0] ? (
            <ResponsiveImage
              rootClassName={css.rootForImage}
              alt=""
              image={images[0]}
              variants={['default']}
              loading={imageLoading}
              fetchpriority={imageFetchPriority}
              decoding="async"
            />
          ) : (
            <div className={css.imagePlaceholder} aria-label="No profile image available"></div>
          )}
          
          {/* IG Logic Commented Out
           {instagramLink ? (
            <div 
              className={css.igBadge} 
              onClick={(e) => {
                e.stopPropagation();
                togglePopup();
              }}
            >
              <AiFillInstagram className={css.icon} aria-hidden="true" />
            </div>
          ) : null}
          */}

          <div className={css.overlayContent}>
            <div className={css.name}>{authorName?.toLowerCase()}</div>
            <div className={css.infoRow}>
              {ageRange && (
                <span className={css.ageOnImage}>
                  {ageRange} y/o
                </span>
              )}
              {/* Always show rating if displayRating is set (for testing) or avgRating exists */}
              {(displayRating || avgRating) && (
                 <div className={css.ratingContainerOverlay}>
                   <span className={css.ratingSeparator}>•</span>
                   <span className={css.ratingValueOverlay}>{displayRating || avgRating}</span>
                   <AiFillStar className={css.starOverlay} />
                 </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {showPopup && <NoIGLinkPopup closePopup={togglePopup} />}
    </div>
  );
}

export default ProductCard;
